import React from 'react';
import { SERIES_STATUS, ISSUE_TAG, StatusBadge } from './status';
import { fileName, num } from './helpers';
import EpisodeTable from './EpisodeTable';
import Thumbnail from './Thumbnail';
import JobProgress from './JobProgress';

const PRE_START_COLUMNS = '40px minmax(0,2fr) 120px minmax(0,1.3fr) 100px 170px';
const STARTED_COLUMNS = '40px minmax(200px,1.6fr) 80px minmax(120px,1.3fr) 190px minmax(170px,1.7fr) 140px';

const issueTone = (issue) => {
    if (issue.severity === 'error') return 'red';
    if (issue.severity === 'info' || issue.code === 'ALREADY_IMPORTED' || issue.code === 'NON_MP4_FILES_IGNORED') return 'gray';
    return 'amber';
};

const Issues = ({ item }) => {
    const issues = Array.isArray(item.validation_issues) ? item.validation_issues : [];
    if (!issues.length && !item.error_message && !item.error_code) return null;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {issues.map((issue, i) => (
                <div key={`${issue.code}-${i}`} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.45 }}>
                    <span className={`bsi-badge ${issueTone(issue)}`} style={{ flexShrink: 0 }}>{ISSUE_TAG[issue.code] || 'Note'}</span>
                    <span>{issue.message}</span>
                </div>
            ))}
            {(item.error_message || item.error_code) && (
                <div className={`bsi-callout ${item.retryable ? 'amber' : 'red'}`} style={{ flexDirection: 'column', gap: 4 }}>
                    {item.error_code && <strong>{item.error_code}</strong>}
                    <span>{item.error_message}</span>
                    {item.retry_hint && <span style={{ color: '#b4b4c4' }}>{item.retry_hint}</span>}
                </div>
            )}
        </div>
    );
};

const nextStep = (item) => {
    if (item.status === 'COMPLETE') {
        return <span style={{ color: '#4ade80' }}>Transcoding complete</span>;
    }
    if (item.error_code || item.error_message || item.retry_hint) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, lineHeight: 1.4, overflowWrap: 'anywhere' }}>
                {item.error_code && <strong style={{ color: '#ff7a6e' }}>{item.error_code}</strong>}
                {item.error_message && <span style={{ color: '#ffc9c3' }}>{item.error_message}</span>}
                {item.retry_hint && <span>{item.retry_hint}</span>}
            </div>
        );
    }
    return <span className="bsi-muted">—</span>;
};

const ExpandButton = ({ item, expanded, onToggle }) => (
    <button type="button" className="bsi-icon-btn" aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.series_title || item.append_path}`} onClick={() => onToggle(item.id)}>
        <i className={`fas fa-chevron-${expanded ? 'down' : 'right'}`} aria-hidden="true" />
    </button>
);

/**
 * One row per series item. Before start: what will be imported and every
 * preview warning. After start: live counts, series navigation, retry hint,
 * and the episode table with per-episode retry.
 */
const SeriesTable = ({
    batch, items, started, canManage, expanded, onToggle,
    onGoToSeries, onRetrySeries, refreshKey, refreshing, onChanged,
}) => (
    <div className="bsi-card" style={{ overflowX: 'auto' }}>
        {!started ? (
            <div className="bsi-row head" style={{ gridTemplateColumns: PRE_START_COLUMNS }}>
                <span /><span className="bsi-th">Series</span><span className="bsi-th">Episodes</span>
                <span className="bsi-th">Trailer</span><span className="bsi-th">Warnings</span><span className="bsi-th">Status</span>
            </div>
        ) : (
            <div className="bsi-row head" style={{ gridTemplateColumns: STARTED_COLUMNS, minWidth: 1100 }}>
                <span /><span className="bsi-th">Series</span><span className="bsi-th">ID</span>
                <span className="bsi-th">Episodes</span><span className="bsi-th">Status</span><span className="bsi-th">Info/Next Step</span><span className="bsi-th">Series details</span>
            </div>
        )}
        {items.length === 0 && <div className="bsi-row bsi-muted">No series in this batch.</div>}
        {items.map((item) => {
            const isOpen = !!expanded[item.id];
            const title = item.series_title || item.append_path;
            const ignored = num(item.episodes_ignored);
            return (
                <React.Fragment key={item.id}>
                    {!started ? (
                        <div className="bsi-row" style={{ gridTemplateColumns: PRE_START_COLUMNS, background: isOpen ? 'rgba(255,255,255,.02)' : undefined }}>
                            <ExpandButton item={item} expanded={isOpen} onToggle={onToggle} />
                            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                                <span className="bsi-title">{title}</span>
                                <span className="bsi-mono bsi-muted" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.append_path}</span>
                            </span>
                            <span style={{ color: '#fff' }}>
                                {item.status === 'VALIDATION_FAILED' ? '—' : num(item.episode_count) - ignored}
                                {ignored > 0 && <span className="bsi-muted"> + {ignored} skip</span>}
                            </span>
                            <span style={{ fontSize: 13, color: item.trailer_fallback ? '#facc15' : undefined }} className={item.trailer_fallback ? '' : 'bsi-mono'}>
                                {!item.trailer_source_uri ? '—' : item.trailer_fallback ? 'First episode (fallback)' : fileName(item.trailer_source_uri)}
                            </span>
                            <span>{num(item.warning_count) > 0 ? <span className="bsi-badge amber">{item.warning_count}</span> : <span className="bsi-muted">—</span>}</span>
                            <span><StatusBadge map={SERIES_STATUS} status={item.status} /></span>
                        </div>
                    ) : (
                        <div className="bsi-row" style={{ gridTemplateColumns: STARTED_COLUMNS, minWidth: 1100, background: isOpen ? 'rgba(255,255,255,.02)' : undefined }}>
                            <ExpandButton item={item} expanded={isOpen} onToggle={onToggle} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                <Thumbnail key={`${item.live_thumbnail_url}:${item.live_poster_url}:${refreshKey}`}
                                    src={item.live_thumbnail_url} fallbackSrc={item.live_poster_url} alt={`${title} thumbnail`} />
                                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                                    <span className="bsi-title">{title}</span>
                                    {canManage && item.retryable && (
                                        <button type="button" className="bsi-link-btn" style={{ fontSize: 13, alignSelf: 'flex-start' }} onClick={() => onRetrySeries([item.id])}>
                                            Retry series
                                        </button>
                                    )}
                                </span>
                            </div>
                            <span className="bsi-mono" style={{ fontSize: 13 }}>{item.series_id ? item.series_id : '—'}</span>
                            <span style={{ fontSize: 13 }}>
                                {num(item.episodes_complete)} done
                                {num(item.episodes_in_progress) > 0 && <span style={{ color: '#38bdf8' }}> · {item.episodes_in_progress} transcoding</span>}
                                {num(item.episodes_failed) > 0 && <span style={{ color: '#ff7a6e' }}> · {item.episodes_failed} failed</span>}
                                {num(item.episodes_cancelled) > 0 && <span className="bsi-muted"> · {item.episodes_cancelled} cancelled</span>}
                                {ignored > 0 && <span className="bsi-muted"> · {ignored} skipped</span>}
                            </span>
                            <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <StatusBadge map={SERIES_STATUS} status={item.status} />
                                <span style={{ fontSize: 12 }}>Cover job: <JobProgress item={item} loading={refreshing} /></span>
                            </span>
                            <div>{nextStep(item)}</div>
                            <div>
                                {item.series_id && (
                                    <button type="button" className="bsi-btn outline" onClick={() => onGoToSeries(item.series_id)}>
                                        Go to Series
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    {isOpen && (
                        <div style={{ padding: '4px 20px 18px 58px', minWidth: started ? 1100 : undefined, display: 'flex', flexDirection: 'column', gap: 14, background: 'rgba(255,255,255,.02)' }}>
                            <Issues item={item} />
                            {item.status !== 'VALIDATION_FAILED' && (
                                <EpisodeTable batchId={batch.id} seriesItem={item} started={started} canManage={canManage}
                                    refreshKey={refreshKey} onChanged={onChanged} />
                            )}
                        </div>
                    )}
                </React.Fragment>
            );
        })}
    </div>
);

export default SeriesTable;
